import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import '../../styles/components/forms.css';

const initialFormState = {
    title: '',
    message: '',
    status: 'draft'
};

export default function BannerManagement() {
    const [formData, setFormData] = useState(initialFormState);
    const [banners, setBanners] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState(initialFormState);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/banner-messages`,
                { withCredentials: true }
            );
            setBanners(response.data || []);
        } catch (err) {
            console.error('Error fetching banner messages:', err);
            setError('Kunne ikke hente bannermeldinger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/banner-messages`,
                formData,
                { withCredentials: true }
            );
            setSuccessMessage('Bannermelding opprettet');
            setFormData(initialFormState);
            await fetchBanners();
        } catch (err) {
            console.error('Error creating banner message:', err);
            setError('Kunne ikke opprette bannermelding');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (banner) => {
        setEditingId(banner.idbanner_message);
        setEditData({
            title: banner.title || '',
            message: banner.message || '',
            status: banner.status || 'draft'
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditData(initialFormState);
    };

    const saveEdit = async (id) => {
        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/banner-messages/${id}`,
                editData,
                { withCredentials: true }
            );
            setSuccessMessage('Bannermelding oppdatert');
            setEditingId(null);
            await fetchBanners();
        } catch (err) {
            console.error('Error updating banner message:', err);
            setError('Kunne ikke oppdatere bannermelding');
        } finally {
            setSaving(false);
        }
    };

    const deleteBanner = async (id) => {
        if (!window.confirm('Er du sikker på at du vil slette denne bannermeldingen?')) {
            return;
        }

        setError('');
        setSuccessMessage('');

        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/banner-messages/${id}`,
                { withCredentials: true }
            );
            setSuccessMessage('Bannermelding slettet');
            await fetchBanners();
        } catch (err) {
            console.error('Error deleting banner message:', err);
            setError('Kunne ikke slette bannermelding');
        }
    };

    const toggleBannerStatus = async (banner) => {
        const nextStatus = banner.status === 'published' ? 'draft' : 'published';

        setError('');
        setSuccessMessage('');

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/banner-messages/${banner.idbanner_message}`,
                {
                    title: banner.title || '',
                    message: banner.message,
                    status: nextStatus
                },
                { withCredentials: true }
            );
            setSuccessMessage(nextStatus === 'published' ? 'Bannermelding publisert' : 'Bannermelding satt til utkast');
            await fetchBanners();
        } catch (err) {
            console.error('Error toggling banner status:', err);
            setError('Kunne ikke oppdatere status');
        }
    };

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Bannermeldinger</h1>
            </div>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <form className="form-container" onSubmit={handleCreate}>
                <div className="form-group">
                    <label>Tittel (valgfri)</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Melding</label>
                    <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="form-textarea"
                        rows="3"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Status</label>
                    <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="form-select"
                    >
                        <option value="draft">Utkast</option>
                        <option value="published">Publisert</option>
                    </select>
                </div>

                <button type="submit" className="submit-button" disabled={saving}>
                    {saving ? 'Lagrer...' : 'Opprett bannermelding'}
                </button>
            </form>

            <div className="activities-list">
                <h2>Eksisterende bannermeldinger</h2>
                <div className="list-container">
                    {loading ? (
                        <div className="list-item">Laster...</div>
                    ) : banners.length === 0 ? (
                        <div className="list-item">Ingen bannermeldinger funnet</div>
                    ) : (
                        banners.map((banner) => (
                            <div key={banner.idbanner_message} className="list-item" style={{ display: 'block' }}>
                                {editingId === banner.idbanner_message ? (
                                    <div>
                                        <div className="form-group">
                                            <label>Tittel (valgfri)</label>
                                            <input
                                                type="text"
                                                value={editData.title}
                                                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                                                className="form-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Melding</label>
                                            <textarea
                                                value={editData.message}
                                                onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                                                className="form-textarea"
                                                rows="3"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select
                                                value={editData.status}
                                                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                                                className="form-select"
                                            >
                                                <option value="draft">Utkast</option>
                                                <option value="published">Publisert</option>
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                type="button"
                                                onClick={() => saveEdit(banner.idbanner_message)}
                                                className="save-button"
                                                disabled={saving}
                                            >
                                                Lagre
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelEdit}
                                                className="cancel-button"
                                                disabled={saving}
                                            >
                                                Avbryt
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <p><strong>Tittel:</strong> {banner.title || 'Ingen tittel'}</p>
                                        <p><strong>Melding:</strong> {banner.message}</p>
                                        <p><strong>Status:</strong> {banner.status === 'published' ? 'Publisert' : 'Utkast'}</p>
                                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                            <button
                                                type="button"
                                                className="edit-button"
                                                onClick={() => startEdit(banner)}
                                            >
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="delete-button"
                                                onClick={() => deleteBanner(banner.idbanner_message)}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="submit-button"
                                                onClick={() => toggleBannerStatus(banner)}
                                                style={{ padding: '0.5rem 1rem' }}
                                            >
                                                {banner.status === 'published' ? 'Sett som utkast' : 'Publiser'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
